
// =============================================================================
//  SCREEN: StatisticsScreen (ohne redundante Navigation)
// =============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../api';
import { toast } from 'react-toastify';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { CLASSIFICATIONS } from '../constants/classificationConstants';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const StatisticsScreen = () => {
  const { id: tournamentId } = useParams();
  const [tournament, setTournament] = useState(null);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedDataType, setSelectedDataType] = useState('totalGesamt');
  const [selectedClassifications, setSelectedClassifications] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tournamentRes, scoresRes] = await Promise.all([
          apiClient.get(`/api/tournaments/${tournamentId}`),
          apiClient.get(`/api/tournaments/${tournamentId}/scores`),
        ]);
        setTournament(tournamentRes.data);
        setScores(scoresRes.data);
        setSelectedClassifications(tournamentRes.data.classifications);
      } catch (err) {
        toast.error('Fehler beim Laden der Statistikdaten.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tournamentId]);

  const handleClassificationChange = (key) => {
    setSelectedClassifications(prev => 
      prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]
    );
  };

  const getClassificationLabel = (key) => CLASSIFICATIONS.find(c => c.key === key)?.label || key;

  const chartData = useMemo(() => {
    if (!scores.length || !tournament) {
      return { labels: [], datasets: [] };
    }

    // GEÄNDERT: Filtere ungültige Scores (ohne Nennung oder Spieler) heraus.
    const filteredScores = scores.filter(score => 
      score.entry && score.entry.player && selectedClassifications.includes(score.entry.classification)
    );

    const laneData = {
      lane1: { totalGesamt: 0, volle: 0, abr: 0, fw: 0, count: 0 },
      lane2: { totalGesamt: 0, volle: 0, abr: 0, fw: 0, count: 0 },
      lane3: { totalGesamt: 0, volle: 0, abr: 0, fw: 0, count: 0 },
      lane4: { totalGesamt: 0, volle: 0, abr: 0, fw: 0, count: 0 },
    };
    
    filteredScores.forEach(score => {
      Object.keys(laneData).forEach(laneKey => {
        if (score[laneKey]) {
          laneData[laneKey].volle += score[laneKey].volle || 0;
          laneData[laneKey].abr += score[laneKey].abr || 0;
          laneData[laneKey].fw += score[laneKey].fw || 0;
          laneData[laneKey].totalGesamt += (score[laneKey].volle || 0) + (score[laneKey].abr || 0);
          if ((score[laneKey].volle || 0) + (score[laneKey].abr || 0) > 0) {
            laneData[laneKey].count += 1;
          }
        }
      });
    });

    const dataMapping = {
      totalGesamt: { label: 'Gesamt gespielt', data: Object.values(laneData).map(d => d.totalGesamt) },
      volle: { label: 'Volle gesamt', data: Object.values(laneData).map(d => d.volle) },
      abr: { label: 'Abräumen gesamt', data: Object.values(laneData).map(d => d.abr) },
      fw: { label: 'Fehlwürfe gesamt', data: Object.values(laneData).map(d => d.fw) },
      avgGesamt: { label: 'Durchschnitt pro Start', data: Object.values(laneData).map(d => d.count > 0 ? (d.totalGesamt / d.count).toFixed(2) : 0) },
    };

    return {
      labels: ['Bahn 1', 'Bahn 2', 'Bahn 3', 'Bahn 4'],
      datasets: [{
        label: dataMapping[selectedDataType].label,
        data: dataMapping[selectedDataType].data,
        backgroundColor: 'rgba(0, 123, 255, 0.6)',
        borderColor: 'rgba(0, 123, 255, 1)',
        borderWidth: 1,
      }],
    };
  }, [scores, selectedClassifications, selectedDataType, tournament]);
  
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: `Bahnanalyse für ${tournament?.name}` }
    }
  };

  if (loading) return <p>Lade Statistik...</p>;
  
  return (
    <div className="screen-container">
      <h2>Statistik: Bahnanalyse</h2>

      <div className="statistics-controls">
        <div className="control-group">
          <h4>Daten anzeigen:</h4>
          <div className="radio-group">
            <label><input type="radio" value="totalGesamt" checked={selectedDataType === 'totalGesamt'} onChange={e => setSelectedDataType(e.target.value)} /> Gesamt</label>
            <label><input type="radio" value="avgGesamt" checked={selectedDataType === 'avgGesamt'} onChange={e => setSelectedDataType(e.target.value)} /> Durchschnitt</label>
            <label><input type="radio" value="volle" checked={selectedDataType === 'volle'} onChange={e => setSelectedDataType(e.target.value)} /> Volle</label>
            <label><input type="radio" value="abr" checked={selectedDataType === 'abr'} onChange={e => setSelectedDataType(e.target.value)} /> Abräumen</label>
            <label><input type="radio" value="fw" checked={selectedDataType === 'fw'} onChange={e => setSelectedDataType(e.target.value)} /> Fehlwürfe</label>
          </div>
        </div>
        <div className="control-group">
          <h4>Wertungen filtern:</h4>
          <div className="checkbox-group">
            {(tournament?.classifications || []).map(key => (
              <div key={key} className="checkbox-item">
                <input type="checkbox" id={`stat-${key}`} checked={selectedClassifications.includes(key)} onChange={() => handleClassificationChange(key)} />
                <label htmlFor={`stat-${key}`}>{getClassificationLabel(key)}</label>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="chart-container">
        <Bar options={chartOptions} data={chartData} />
      </div>
    </div>
  );
};

export default StatisticsScreen;